/**
 * PKCE (Proof Key for Code Exchange) utilities.
 *
 * These functions are used in OAuth 2.0 Authorization Code Flow with PKCE
 * to securely authenticate public clients (e.g. React SPA, mobile apps)
 * without exposing a client secret.
 *
 * ---------------------------------------------------------
 *  HOW PKCE WORKS (high-level)
 * ---------------------------------------------------------
 * 1. Client generates a random `code_verifier` (secret).
 * 2. From it, a `code_challenge` is derived using SHA-256 + Base64URL.
 * 3. `code_challenge` is sent in the /authorize request.
 * 4. After login, client receives an authorization `code`.
 * 5. Client sends `code` + original `code_verifier` to /token endpoint.
 * 6. Server verifies:
 *        SHA256(code_verifier) === code_challenge
 *
 * This ensures that only the original client that initiated the flow
 * can exchange the authorization code for tokens.
 *
 * ---------------------------------------------------------
 *  generateCodeVerifier
 * ---------------------------------------------------------
 * Generates a cryptographically secure random string used as a secret.
 *
 * - Length: 43–128 characters (default: 64)
 * - Charset: A-Z a-z 0-9 - . _ ~ (PKCE compliant)
 * - Uses `crypto.getRandomValues` for secure randomness
 *
 * This value must be:
 * - stored temporarily (e.g. sessionStorage)
 * - sent ONLY in the /token request (never in /authorize)
 *
 * ---------------------------------------------------------
 *  generateCodeChallenge
 * ---------------------------------------------------------
 * Derives a `code_challenge` from the given `code_verifier`.
 *
 * Steps:
 * 1. Encode verifier string into bytes (UTF-8)
 * 2. Apply SHA-256 hash → 32-byte digest
 * 3. Convert bytes to binary string
 * 4. Encode to Base64
 * 5. Transform Base64 → Base64URL (PKCE requirement)
 *
 * Output:
 * - URL-safe string sent as `code_challenge`
 * - Used by server to verify the original verifier
 *
 * ---------------------------------------------------------
 *  SECURITY NOTES
 * ---------------------------------------------------------
 * - `code_verifier` is a temporary secret → store in sessionStorage
 * - Do NOT expose verifier in URLs or logs
 * - Always use `code_challenge_method=S256`
 * - Do NOT use Math.random() (must use crypto API)
 *
 * ---------------------------------------------------------
 *  FLOW SUMMARY
 * ---------------------------------------------------------
 * verifier → SHA256 → base64 → base64url → challenge
 */

const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
const SHA_256_KEY = 'SHA-256';

export function generateCodeVerifier(length = 64) {
  const randomValues = new Uint8Array(length);
  window.crypto.getRandomValues(randomValues);
  let verifier = '';
  for (let i = 0; i < randomValues.length; i++) {
    verifier += charset[randomValues[i] % charset.length];
  }

  return verifier;
}

export async function generateCodeChallenge(verifier: string) {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest(SHA_256_KEY, data);

  const bytes = new Uint8Array(digest);
  let binary = '';

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
